/**
 * Registry per mappare endpoint form ai modali corrispondenti
 *
 * Formato endpoint: form/{entity}/{subtype}/{action}
 * Esempio: form/utenti/discenti/new
 *
 * action: "new" = creazione, altrimenti è l'id per edit
 */

import NiceModal from "@ebay/nice-modal-react";
import { isPageEndpoint } from "./pageRegistry";

// Lazy import dei modali per evitare circular dependencies
const modalMap: Record<string, () => Promise<any>> = {
  "form/users/discenti": () => import("@/modules/Users/components/CreateDiscenteModal"),
  "form/users/attori_didattici": () => import("@/modules/Users/components/CreateUserModal"),
  "form/users/consulenti": () => import("@/modules/Users/components/CreateConsulenteModal"),
  "form/users/enti": () => import("@/modules/Users/components/CreateEnteModal"),
  "form/users/soggetti_attuatori": () => import("@/modules/Users/components/SoggettoAttuatoreModal"),
  "form/users/collaboratori": () => import("@/modules/Users/Collaborators/components/NewCollaboratorModal"),
  // Materiale Didattico - endpoint attesi: form/materiale_didattico/{tipo}
  "form/materiale_didattico/scorm": () => import("@/modules/Didattica/MaterialeDidattico/modals/MaterialeDidatticoModal"),
  "form/materiale_didattico/video": () => import("@/modules/Didattica/MaterialeDidattico/modals/MaterialeDidatticoModal"),
  "form/materiale_didattico/pdf": () => import("@/modules/Didattica/MaterialeDidattico/modals/MaterialeDidatticoModal"),
  "form/categoria_corsi/0": () =>
    import("@/modules/Didattica/Categorie/components/CategoriaCorsoModal"),
};

interface FormEndpointParts {
  basePath: string;  // es. "form/utenti/discenti"
  action: string;    // es. "new" o un ID
  isNew: boolean;
}

/**
 * Parsa un endpoint form
 * @param endpoint es. "form/utenti/discenti/new" o "form/utenti/discenti/123"
 */
export function parseFormEndpoint(endpoint: string): FormEndpointParts | null {
  const parts = endpoint.split("/");
  if (parts.length < 4 || parts[0] !== "form") {
    return null;
  }

  const action = parts[parts.length - 1];
  const basePath = parts.slice(0, -1).join("/");

  return {
    basePath,
    action,
    isNew: action === "new",
  };
}

/**
 * Estrae il tipo di materiale dall'endpoint
 * @param basePath es. "form/materiale_didattico/scorm"
 */
function getMaterialTypeFromEndpoint(basePath: string): 'video' | 'scorm' | 'pdf' | undefined {
  if (basePath.includes('materiale_didattico')) {
    const parts = basePath.split('/');
    const type = parts[parts.length - 1];
    if (type === 'video' || type === 'scorm' || type === 'pdf') {
      return type;
    }
  }
  return undefined;
}

/**
 * Props aggiuntive che possono essere passate ai modali
 */
export interface FormModalExtraProps {
  onDelete?: (id: string) => Promise<void>;
  [key: string]: any;
}

/**
 * Apre il modale corrispondente all'endpoint
 * @param endpoint es. "form/utenti/discenti/new"
 * @param extraProps Props aggiuntive da passare al modale (es. onDelete)
 */
export async function openFormModal(
  endpoint: string,
  extraProps?: FormModalExtraProps
): Promise<boolean> {
  const parsed = parseFormEndpoint(endpoint);
  if (!parsed) {
    console.warn(`Invalid form endpoint: ${endpoint}`);
    return false;
  }

  const modalLoader = modalMap[parsed.basePath];
  if (!modalLoader) {
    console.warn(`No modal registered for: ${parsed.basePath}`);
    return false;
  }

  try {
    const modalModule = await modalLoader();
    const ModalComponent = modalModule.default;

    // Props per il modale
    const modalProps: Record<string, any> = {
      ...extraProps, // Props aggiuntive (es. onDelete)
    };

    // Per edit, passa l'ID
    if (!parsed.isNew) {
      modalProps.id = parsed.action;
    }

    // Per materiale didattico, passa anche il tipo
    const materialType = getMaterialTypeFromEndpoint(parsed.basePath);
    if (materialType) {
      modalProps.materialType = materialType;
    }

    NiceModal.show(ModalComponent, modalProps);
    return true;
  } catch (error) {
    console.error(`Failed to load modal for: ${parsed.basePath}`, error);
    return false;
  }
}

/**
 * Verifica se un endpoint è un form endpoint (modale, NON pagina)
 * Esclude gli endpoint che sono mappati a pagine full-screen nel pageRegistry
 */
export function isFormEndpoint(endpoint: string): boolean {
  if (!endpoint.startsWith("form/")) {
    return false;
  }
  // Se è un endpoint pagina (es. form/corsi/fad), non è un form modale
  if (isPageEndpoint(endpoint)) {
    return false;
  }
  return true;
}
